import { notifyError } from "@/lib/notify-error";
import { useRouterState } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { LogOut } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@constructionautomation/backend";

import { useUserProfile } from "@/components/auth";
import { Button } from "@/components/ui/button";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { cn } from "@/lib/utils";
import { TopNav } from "./nav-header";

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { userProfile } = useUserProfile();
  const stopEmulation = useMutation(api.admin.organizations.stopEmulation);
  const navigate = useAppNavigate();
  const [isLeavingSupportSession, setIsLeavingSupportSession] = useState(false);
  const isEmulatingOrganization = userProfile?.isEmulatingOrganization === true;
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const isTakeoffPilotJobRoute = /^\/takeoffPilot\/(?!new$)[^/]+$/.test(pathname);
  const isFieldPilotJobRoute = /^\/fieldPilot\/(?!new$)[^/]+$/.test(pathname);
  const isFullBleedRoute =
    pathname.startsWith("/revCollab/revisions/") ||
    isTakeoffPilotJobRoute ||
    isFieldPilotJobRoute;

  const leaveSupportSession = async () => {
    setIsLeavingSupportSession(true);
    try {
      await stopEmulation({});
      toast.success("Left organization support session");
      navigate({ to: "/admin/organizations", title: "Organizations" });
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to leave support session");
    } finally {
      setIsLeavingSupportSession(false);
    }
  };

  return (
    <div
      data-slot="app-shell"
      className="relative flex h-dvh w-full flex-col overflow-hidden bg-background pb-[env(safe-area-inset-bottom)]"
    >
      {!isTakeoffPilotJobRoute && !isFieldPilotJobRoute && <TopNav />}

      {/* Page Content: a flex column so full-height pages (PDF review, document viewer) fill it with flex-1 */}
      <main
        data-slot="page-main"
        className={cn(
          "flex-1",
          isFullBleedRoute ? "overflow-hidden" : "overflow-y-auto"
        )}
      >
        {isFullBleedRoute ? (
          children
        ) : (
          <div data-slot="page-content" className="flex min-h-full flex-col p-6 lg:p-8">
            {children}
          </div>
        )}
      </main>

      {isEmulatingOrganization ? (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none fixed inset-0 z-[60] border-4 border-amber-500"
          />
          <div
            className="fixed inset-x-4 bottom-4 z-[70] mx-auto flex w-fit max-w-[calc(100vw-2rem)] items-center gap-3 rounded-lg border-2 border-amber-600 bg-amber-100 px-3 py-2 text-sm text-amber-950 shadow-lg dark:bg-amber-950 dark:text-amber-50"
            role="status"
          >
          <span className="min-w-0 font-medium">
            Assisting {userProfile.organizationName ?? "this organization"} as an admin
          </span>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 border-amber-700 bg-transparent text-amber-950 hover:bg-amber-200 dark:text-amber-50 dark:hover:bg-amber-900"
            disabled={isLeavingSupportSession}
            onClick={() => void leaveSupportSession()}
          >
            <LogOut className="size-4" aria-hidden="true" />
            Leave
          </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/*
 * Usage Example:
 *
 * // In __root.tsx
 * import { AppShell } from "@/components/layout";
 *
 * export const Route = createRootRoute({
 *   component: () => (
 *     <AppShell>
 *       <Outlet />
 *     </AppShell>
 *   ),
 * });
 */
